import { useEffect, useMemo, useState } from 'react';
import {
  FluentProvider,
  webLightTheme,
  Spinner,
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  Avatar,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowExit24Regular } from '@fluentui/react-icons';
import { useAuth } from '../auth/useAuth';
import { useUserPortal } from '../hooks/useUserPortal';
import { Sidebar } from './Sidebar';
import { UserManagementPage } from './pages/UserManagementPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { ReportsPage } from './pages/ReportsPage';
import { PartnerManagementPage } from './pages/PartnerManagementPage';
import { WidgetConfigPage } from './pages/WidgetConfigPage';
import { ConfigStorePage } from './pages/ConfigStorePage';
import { ConversationDiagnosticsPage } from './pages/ConversationDiagnosticsPage';
import { RoutingDecisionsPage } from './pages/RoutingDecisionsPage';
import { ObservabilityPage } from './pages/ObservabilityPage';

const useStyles = makeStyles({
  // `height` (not `minHeight`) is what makes the shell a real app shell: it bounds the column so
  // `body`'s `minHeight: 0` and `content`'s `overflow: auto` below can actually take effect. With
  // `minHeight` the root grew past the viewport, the document scrolled instead of the content area,
  // and any page trying to fill the remaining space had to guess at pixel offsets to do it.
  root: { display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: tokens.colorNeutralBackground1 },
  body: { display: 'flex', flex: 1, minHeight: 0 },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 },
  // The content area is a DOCK: one fill row, so a page that asks for `height: 100%` gets exactly
  // the space between header and viewport bottom and sizes its own internals against that. Pages
  // that don't (the older admin pages) sit in the same row at their natural height and overflow it,
  // which this element's `overflow: auto` then scrolls — so docking is opt-in and nothing regresses.
  content: {
    padding: tokens.spacingHorizontalXXL,
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr)',
  },
  centered: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '24px' },
});

// Brand header matching the Consumer Support Portal's blue gradient theme (kept in sync visually with
// components/Header.tsx, which is intentionally left unchanged). Rendered with inline styles to mirror
// that component exactly and avoid Griffel numeric-value pitfalls.
const brandStyles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 10,
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #0078d4 0%, #1a5fb4 100%)',
    color: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    color: 'white',
    textAlign: 'left',
  },
  logo: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, width: 32, height: 32 },
  logoBlock: { width: '100%', height: '100%', borderRadius: 1 },
  brandSubtitle: { fontSize: 11, fontWeight: 600, letterSpacing: 1, opacity: 0.9 },
  brandTitle: { fontSize: 22, fontWeight: 700 },
  actions: { display: 'flex', alignItems: 'center', gap: 12 },
  user: { display: 'flex', alignItems: 'center', gap: 8, color: 'white' },
  pillButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    borderRadius: 999,
    padding: '7px 14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: 14,
  },
  iconButton: {
    display: 'inline-grid',
    placeItems: 'center',
    width: 34,
    height: 34,
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    borderRadius: 999,
    cursor: 'pointer',
  },
};

// Reads the admin sub-route from the hash, e.g. "#/admin/users" -> "/admin/users".
// Strips any query string (e.g. "#/admin/widgets?partner=xbox") so route matching is exact.
function readAdminPath(): string {
  const hash = window.location.hash.replace(/^#/, '');
  const path = hash.split('?')[0];
  return path || '/admin';
}

/**
 * The admin portal — a Fluent UI shell with a permission-driven left navigation (Sidebar) and a
 * routed content area. Mounted for the `#/admin...` hash routes. Access is entirely permission
 * driven: the menu (and the pages) come from the user's granted permissions, resolved server-side.
 */
export function AdminPortal() {
  const styles = useStyles();
  const { isSignedIn, signIn, signOut, account } = useAuth();
  const { loading, menu, userInfo, hasPortalAccess, permissions } = useUserPortal();
  const [path, setPath] = useState(readAdminPath());

  useEffect(() => {
    const handler = () => setPath(readAdminPath());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const navigate = (url: string) => {
    window.location.hash = url.replace(/^#/, '');
  };

  const displayName = userInfo?.displayName ?? account?.name ?? account?.username ?? 'User';

  const canViewRoutingDecisions = permissions.includes('Admin.Partners');
  const content = useMemo(() => renderRoute(path, canViewRoutingDecisions), [path, canViewRoutingDecisions]);

  if (!isSignedIn) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div className={styles.centered}>
          <div style={{ textAlign: 'center' }}>
            <Text as="h1" size={700} weight="semibold" style={{ display: 'block', marginBottom: 12 }}>
              Admin Portal
            </Text>
            <Text style={{ display: 'block', marginBottom: 20 }}>Sign in to access administration and reports.</Text>
            <Button appearance="primary" onClick={signIn}>Sign in with Microsoft</Button>
          </div>
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={styles.root}>
        <BrandHeader displayName={displayName} onSignOut={signOut} />
        <div className={styles.body}>
          <Sidebar menu={menu} activeUrl={`#${path}`} onNavigate={navigate} />
          <div className={styles.main}>
            <main className={styles.content}>
              {loading ? (
                <Spinner label="Loading your workspace…" />
              ) : !hasPortalAccess ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    You don't have access to any admin sections. Contact an administrator to request permissions.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                content
              )}
            </main>
          </div>
        </div>
      </div>
    </FluentProvider>
  );
}

/**
 * The blue gradient brand header shared visually with the Consumer Support Portal. Clicking the brand
 * returns to the portal. The admin actions (back link, user, sign out) sit on the right.
 */
function BrandHeader({ displayName, onSignOut }: { displayName: string; onSignOut: () => void }) {
  return (
    <header style={brandStyles.header}>
      <button style={brandStyles.brand} onClick={() => { window.location.hash = '#/'; }}>
        <div style={brandStyles.logo}>
          <span style={{ ...brandStyles.logoBlock, background: '#f25022' }} />
          <span style={{ ...brandStyles.logoBlock, background: '#7fba00' }} />
          <span style={{ ...brandStyles.logoBlock, background: '#00a4ef' }} />
          <span style={{ ...brandStyles.logoBlock, background: '#ffb900' }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={brandStyles.brandSubtitle}>ONECONSUMER SUPPORT</div>
          <div style={brandStyles.brandTitle}>Consumer Support Portal</div>
        </div>
      </button>
      <div style={brandStyles.actions}>
        <button style={brandStyles.pillButton} onClick={() => { window.location.hash = '#/'; }}>
          ← Back to portal
        </button>
        <div style={brandStyles.user}>
          <Avatar name={displayName} size={28} color="colorful" />
          <Text size={300} style={{ color: 'white' }}>{displayName}</Text>
        </div>
        <button style={brandStyles.iconButton} onClick={onSignOut} aria-label="Sign out" title="Sign out">
          <ArrowExit24Regular />
        </button>
      </div>
    </header>
  );
}

function renderRoute(path: string, canViewRoutingDecisions: boolean): React.ReactElement {
  switch (path) {
    case '/admin/users':
      return <UserManagementPage />;
    case '/admin/permissions':
      return <PermissionsPage />;
    case '/admin/partners':
      return <PartnerManagementPage />;
    case '/admin/widgets':
      return <WidgetConfigPage />;
    case '/admin/config-store':
      return <ConfigStorePage canViewRoutingDecisions={canViewRoutingDecisions} />;
    case '/admin/routing-decisions':
      return canViewRoutingDecisions ? <RoutingDecisionsPage /> : (
        <MessageBar intent="warning">
          <MessageBarBody>You need the Admin.Partners permission to view routing decisions.</MessageBarBody>
        </MessageBar>
      );
    case '/admin/diagnostics/conversation':
      return <ConversationDiagnosticsPage />;
    case '/admin/observability':
      return <ObservabilityPage />;
    case '/admin/reports/usage':
      return <ReportsPage variant="usage" />;
    case '/admin/reports/csat':
      return <ReportsPage variant="csat" />;
    default:
      return <AdminHome />;
  }
}

function AdminHome() {
  return (
    <section>
      <Text as="h1" size={600} weight="semibold" style={{ display: 'block', marginBottom: 8 }}>
        Welcome
      </Text>
      <Text>Select a section from the navigation on the left to get started.</Text>
    </section>
  );
}
